/**
 * Chip-input model — pure, framework-free core (E10-F02-T04).
 *
 * Parity with the legacy `project.js` CC-input (`initCcInput` / `addCcChip` /
 * `removeCcChip` / `_renderCcChips` / `_getCcChips` / `_saveCcEmails`) AND the
 * Research-intake chip fields (competitors / DNS / targeting tags) from the
 * prototype. Both share the same interaction model; the only difference is
 * whether the entered value is treated as an email (normalised + validated) or
 * as a free-text tag.
 *
 * The legacy CC code did exactly this (`addCcChip`):
 *   - `email.trim().toLowerCase()`            → normalise
 *   - reject `''`                              → empty guard
 *   - regex `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`     → validate, else toast
 *     'Invalid email: ' + value, type 'error'
 *   - skip if `_ccEmails.includes(trimmed)`    → no duplicates
 *   - on Enter / ',' : `input.value.trim().replace(/,$/, '')` then add
 *   - on Backspace with empty input: pop the last chip
 *   - on blur: commit the current input value
 *   - CC-mode persisted the list via `update_project { cc_emails }` and updated
 *     local `state.currentProject.data.cc_emails` immediately.
 *
 * This module owns the value semantics (normalise / validate / dedup / commit)
 * so they are trivially unit-testable and reused by both the React component
 * ({@link ChipInput}) and any non-React caller. React state, the persistence
 * call (CC auto-save) and the toast are wired by the component / its consumer.
 */

/**
 * Email validation regex — copied verbatim from the legacy CC input
 * (`/^[^\s@]+@[^\s@]+\.[^\s@]+$/`): one-or-more non-space/non-@ chars, an `@`,
 * a domain label, a dot, and a TLD. Intentionally permissive (same as legacy)
 * so behaviour matches the old app exactly.
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The two value modes a chip-input can run in.
 *   - `'email'`     — CC / address-book style: lowercase-normalise + regex
 *     validate + reject invalid with a toast (parity with `addCcChip`).
 *   - `'free-text'` — tags / dictionaries (Research-intake competitors, DNS,
 *     targeting): trim only, no email validation, no lowercasing beyond the
 *     caller's choice. Duplicates are still rejected (a chip set is a set).
 */
export type ChipMode = 'email' | 'free-text';

/** Outcome of attempting to commit a raw input string into the chip set. */
export type ChipCommit =
  | {
      /** The value was accepted; `value` is the normalised chip to append. */
      readonly ok: true;
      readonly value: string;
    }
  | {
      /** The value was rejected; `value` is the unchanged chip set. */
      readonly ok: false;
      /** Why it was rejected (drives whether the caller raises a toast). */
      readonly reason: ChipRejectReason;
      /**
       * Error message to surface (only set for {@link ChipRejectReason.Invalid}
       * — duplicates and empties are silent, exactly like the legacy code).
       */
      readonly message?: string;
    };

/** Reason a commit was rejected. */
export enum ChipRejectReason {
  /** Input was empty after trim / comma-strip — silently ignored. */
  Empty = 'empty',
  /** Email mode: value failed {@link EMAIL_REGEX} — surfaced via toast. */
  Invalid = 'invalid',
  /** Value already present in the chip set — silently ignored (no dupes). */
  Duplicate = 'duplicate',
}

/**
 * Clean a raw input string before it becomes a chip.
 *
 * Parity with the legacy keydown/blur handlers, which did
 * `input.value.trim().replace(/,$/, '')` (strip ONE trailing comma — the comma
 * key fires the commit so the value can end with the just-typed `,`) and then
 * `addCcChip` did a further `.trim().toLowerCase()`.
 *
 * In `'email'` mode we lowercase (CC parity); in `'free-text'` mode we preserve
 * case (tags / proper nouns like competitor names keep their casing).
 */
export function normalizeChipValue(raw: string, mode: ChipMode): string {
  // Strip a single trailing comma first (the commit key), THEN trim, so
  // "  foo@bar.com,  " → "foo@bar.com" regardless of where the comma sits.
  const stripped = raw.replace(/,\s*$/, '').trim();
  return mode === 'email' ? stripped.toLowerCase() : stripped;
}

/** True when `value` is a syntactically valid email per {@link EMAIL_REGEX}. */
export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

/**
 * Attempt to commit a raw input string into the existing chip set.
 *
 * Pure: returns the decision; never mutates `chips`. The caller applies the
 * `value` (appends it) on `ok`, and on `!ok` with reason {@link ChipRejectReason.Invalid}
 * raises an error toast with `message` (mirrors `showNotification('Invalid email: …','error')`).
 *
 * Order of checks (legacy `addCcChip` order):
 *   1. normalise (trim + trailing-comma strip + lowercase-in-email-mode);
 *   2. empty → reject silently;
 *   3. email mode: regex → reject with message if invalid;
 *   4. duplicate (case-insensitive in email mode, exact in free-text) → reject silently.
 */
export function commitChipInput(raw: string, chips: readonly string[], mode: ChipMode): ChipCommit {
  const value = normalizeChipValue(raw, mode);

  if (!value) {
    return { ok: false, reason: ChipRejectReason.Empty };
  }

  if (mode === 'email' && !isValidEmail(value)) {
    return {
      ok: false,
      reason: ChipRejectReason.Invalid,
      message: `Invalid email: ${value}`,
    };
  }

  // Duplicate check. Email mode is already lowercased on both sides; free-text
  // compares exactly (a tag set is a set of distinct strings).
  if (chips.includes(value)) {
    return { ok: false, reason: ChipRejectReason.Duplicate };
  }

  return { ok: true, value };
}

/** Remove a chip by exact value. Pure: returns a new array. */
export function removeChip(chips: readonly string[], value: string): string[] {
  return chips.filter((c) => c !== value);
}

/** The keyboard intent a keydown maps to inside the chip-input field. */
export type ChipKeyIntent =
  /** Enter / comma with text present → commit the current value as a chip. */
  | 'commit'
  /** Backspace on an EMPTY field → pop the last chip. */
  | 'remove-last'
  /** Anything else → let the input handle it normally. */
  | 'none';

/**
 * Map a keydown to a chip intent (parity with the legacy `input.onkeydown`):
 *   - `Enter` or `,`              → 'commit' (the handler preventDefaults + adds);
 *   - `Backspace` on empty input  → 'remove-last' (pop the last chip);
 *   - otherwise                    → 'none'.
 *
 * `currentValue` is the input's live value; an empty/whitespace-only value is
 * what makes Backspace remove a chip (legacy: `else if (key==='Backspace' && !input.value)`).
 */
export function chipKeyIntent(key: string, currentValue: string): ChipKeyIntent {
  if (key === 'Enter' || key === ',') return 'commit';
  if (key === 'Backspace' && currentValue === '') return 'remove-last';
  return 'none';
}
